/**
 * Network Speed Detection Utility
 * Detects network connection speed and quality using Network Information API
 */

/**
 * Network speed categories
 */
export const NetworkSpeed = {
  VERY_SLOW: 'very-slow', // slow-2g, 2g
  SLOW: 'slow', // 3g
  MEDIUM: 'medium', // 4g
  FAST: 'fast', // 4g+, wifi
  UNKNOWN: 'unknown',
};

/**
 * Get effective connection type from Network Information API
 * @returns {string} Effective connection type (slow-2g, 2g, 3g, 4g)
 */
export function getEffectiveConnectionType() {
  if (!navigator.connection) {
    console.warn('⚠️ Network Information API not supported');
    return null;
  }

  const connection = navigator.connection;
  return connection.effectiveType || null;
}

/**
 * Get downlink speed in Mbps
 * @returns {number|null} Downlink speed in Mbps
 */
export function getDownlinkSpeed() {
  if (!navigator.connection) {
    return null;
  }

  const connection = navigator.connection;
  return connection.downlink || null;
}

/**
 * Detect network speed category
 * @returns {string} Network speed category (NetworkSpeed enum)
 */
export function detectNetworkSpeed() {
  const effectiveType = getEffectiveConnectionType();
  const downlink = getDownlinkSpeed();

  console.log(`📡 Network detection: effectiveType=${effectiveType}, downlink=${downlink}Mbps`);

  // If API not available, assume medium speed
  if (!effectiveType) {
    console.log('📡 Network API unavailable, assuming MEDIUM speed');
    return NetworkSpeed.UNKNOWN;
  }

  // Categorize based on effective type
  switch (effectiveType) {
    case 'slow-2g':
    case '2g':
      console.log('📡 Detected VERY_SLOW network (2G)');
      return NetworkSpeed.VERY_SLOW;

    case '3g':
      console.log('📡 Detected SLOW network (3G)');
      return NetworkSpeed.SLOW;

    case '4g':
      // 4G can vary widely, use downlink if available
      if (downlink && downlink < 1.5) {
        console.log('📡 Detected SLOW network (4G with low downlink)');
        return NetworkSpeed.SLOW;
      } else if (downlink && downlink >= 5) {
        console.log('📡 Detected FAST network (4G with high downlink)');
        return NetworkSpeed.FAST;
      } else {
        console.log('📡 Detected MEDIUM network (4G)');
        return NetworkSpeed.MEDIUM;
      }

    default:
      console.log('📡 Detected MEDIUM network (unknown type)');
      return NetworkSpeed.MEDIUM;
  }
}

/**
 * Check if network speed is slow (VERY_SLOW or SLOW)
 * @returns {boolean} True if network is slow
 */
export function isSlowNetwork() {
  const speed = detectNetworkSpeed();
  return speed === NetworkSpeed.VERY_SLOW || speed === NetworkSpeed.SLOW;
}

/**
 * Get recommended video quality based on network speed
 * @returns {string} Recommended quality ('low', 'medium', 'high', 'auto')
 */
export function getRecommendedVideoQuality() {
  const speed = detectNetworkSpeed();

  switch (speed) {
    case NetworkSpeed.VERY_SLOW:
      console.log('📡 Recommended quality: low');
      return 'low';

    case NetworkSpeed.SLOW:
      console.log('📡 Recommended quality: medium');
      return 'medium';

    case NetworkSpeed.MEDIUM:
      console.log('📡 Recommended quality: high');
      return 'high';

    case NetworkSpeed.FAST:
      console.log('📡 Recommended quality: auto (highest)');
      return 'auto';

    case NetworkSpeed.UNKNOWN:
    default:
      console.log('📡 Recommended quality: auto (unknown network)');
      return 'auto';
  }
}

/**
 * Get adaptive HLS configuration based on network speed
 * @returns {Object} HLS configuration overrides
 */
export function getAdaptiveHLSConfig() {
  const speed = detectNetworkSpeed();

  const baseConfig = {
    enableWorker: true,
    lowLatencyMode: false,
  };

  switch (speed) {
    case NetworkSpeed.VERY_SLOW:
      console.log('📡 Applying VERY_SLOW network HLS config');
      return {
        ...baseConfig,
        maxBufferLength: 10, // 10秒先読み（最小限）
        maxMaxBufferLength: 20, // 最大20秒
        abrBandWidthFactor: 0.8, // 安全マージン大きめ
        fragLoadingMaxRetry: 8, // リトライ回数増加
        fragLoadingTimeOut: 30000, // タイムアウト延長
      };

    case NetworkSpeed.SLOW:
      console.log('📡 Applying SLOW network HLS config');
      return {
        ...baseConfig,
        maxBufferLength: 15, // 15秒先読み
        maxMaxBufferLength: 30, // 最大30秒
        abrBandWidthFactor: 0.85, // 安全マージン
        fragLoadingMaxRetry: 6,
        fragLoadingTimeOut: 25000,
      };

    case NetworkSpeed.MEDIUM:
      console.log('📡 Applying MEDIUM network HLS config');
      return {
        ...baseConfig,
        maxBufferLength: 20,
        maxMaxBufferLength: 40,
        abrBandWidthFactor: 0.9,
        fragLoadingMaxRetry: 4,
        fragLoadingTimeOut: 20000,
      };

    case NetworkSpeed.FAST:
      console.log('📡 Applying FAST network HLS config');
      return {
        ...baseConfig,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        abrBandWidthFactor: 0.95,
        fragLoadingMaxRetry: 4,
        fragLoadingTimeOut: 15000,
      };

    case NetworkSpeed.UNKNOWN:
    default:
      console.log('📡 Applying DEFAULT HLS config (unknown network)');
      return {
        ...baseConfig,
        maxBufferLength: 20, // デフォルト: 中程度
        maxMaxBufferLength: 40,
        abrBandWidthFactor: 0.9,
        fragLoadingMaxRetry: 4,
        fragLoadingTimeOut: 20000,
      };
  }
}

/**
 * Monitor network changes and notify callback
 * @param {Function} callback - Callback function (speed: NetworkSpeed) => void
 * @returns {Function} Cleanup function
 */
export function monitorNetworkChanges(callback) {
  if (!navigator.connection) {
    console.warn('⚠️ Network monitoring not supported');
    return () => {};
  }

  const connection = navigator.connection;

  const handleChange = () => {
    const newSpeed = detectNetworkSpeed();
    console.log(`📡 Network changed to: ${newSpeed}`);
    callback(newSpeed);
  };

  connection.addEventListener('change', handleChange);

  // Return cleanup function
  return () => {
    connection.removeEventListener('change', handleChange);
  };
}
